/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Render the live board grouped by who needs the teacher.
 * @state-owner dataset store; navigation store owns filters
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks; sibling dialogs
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects opens the intervention screen and the end-session dialog
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { MouseEvent } from 'react';
import { MenuRows } from '../../../shared/components/ContextMenu';
import { FilterBar } from '../../../shared/components/FilterBar';
import { pointerPoint, useOverlay } from '../../../shared/components/OverlayHost';
import { GroupHeading, Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { NameCell, ProgressBars, StatusText } from '../../../shared/components/StatusIcon';
import { GearIcon, QrIcon } from '../../../shared/components/VispoIcon';
import { ToolbarIconButton, tableCells, VTable } from '../../../shared/components/VTable';
import { SEAT_LABEL } from '../../../shared/lib/demo-data';
import { filterDefinitions } from '../../../shared/lib/filters';
import type { RunRecord, SeatRecord } from '../../../shared/lib/model';
import {
  findActivity,
  myClassIndexes,
  runName,
  seatPresentation,
  seatQuestion,
  seatStatusLabel,
  seatUrgency,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { QrInviteDialog } from '../../classrooms';
import { useSessionMinutes } from '../model/use-session-minutes';
import {
  isMuted,
  type SeatAlert,
  SNOOZE_MINUTES,
  selectAlert,
  useSeatAlerts,
} from '../store/seat-alerts';
import { AgentPolicyDialog } from './AgentPolicyDialog';
import { EndSessionDialog } from './EndSessionDialog';
import { SeatSignals } from './SeatSignals';

export function RunDashboard({ run }: { run: RunRecord }) {
  const t = useTranslator();
  const { openModal, openPopover } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const seats = useDatasetStore((state) => state.seats[run.classIndex] ?? []);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const alerts = useSeatAlerts((state) => state.alerts);
  const setAlert = useSeatAlerts((state) => state.setAlert);
  const reopen = useSeatAlerts((state) => state.reopen);
  /**
   * Minutes into the session. The seats carry their own freshness, so this is only the clock a
   * snooze counts against; it does not need to be exact to the second.
   */
  const elapsed = useSessionMinutes();

  const total = findActivity(activities, run.activityId)?.questionCount ?? 0;
  const count = (status: SeatRecord['status']) =>
    seats.filter((seat) => seat.status === status).length;
  const needing = count('need') + count('stuck');

  const alertOf = (seat: SeatRecord) => selectAlert(alerts, run.classIndex, seat.name);
  const muted = (seat: SeatRecord) => isMuted(alertOf(seat), elapsed);
  const flagged = (seat: SeatRecord) => seatPresentation(seat.status).needsTeacher;

  /**
   * Pinned first, then anything still asking for the teacher. What they have already acknowledged,
   * snoozed or ignored drops to its own group instead of pushing back into the queue.
   */
  const loose = (seat: SeatRecord) => alertOf(seat).state !== 'pinned';
  /** Ordered by `seatUrgency`, the one place that says which status shouts loudest. */
  const byStatus = (['ok', 'online', 'offline'] as const)
    .slice()
    .sort((a, b) => seatUrgency(b) - seatUrgency(a))
    .map(
      (status) =>
        [
          t(SEAT_LABEL[status]),
          (seat: SeatRecord) => seat.status === status && loose(seat),
        ] as const,
    );

  const groups: readonly (readonly [string, (seat: SeatRecord) => boolean])[] = [
    [t('已釘選'), (seat) => alertOf(seat).state === 'pinned'],
    [t('需要你'), (seat) => flagged(seat) && loose(seat) && !muted(seat)],
    ...byStatus,
    [t('已處理'), (seat) => flagged(seat) && loose(seat) && muted(seat)],
  ];

  /** All alert actions in one menu, so the row keeps a single affordance. */
  const openAlertMenu = (event: MouseEvent<HTMLElement>, seat: SeatRecord) => {
    event.preventDefault();
    event.stopPropagation();
    const point = pointerPoint(event);
    const alert = alertOf(seat);
    const act = (state: SeatAlert['state'], message: string, until?: number) => {
      setAlert(run.classIndex, seat.name, { state, until });
      notify(`${seat.name} ${message}`);
    };
    openPopover({
      x: point.x,
      y: point.y,
      className: 'cmenu rmenu',
      content: (
        <MenuRows
          items={[
            {
              label: alert.state === 'pinned' ? t('取消釘選') : t('釘選'),
              onSelect: () =>
                alert.state === 'pinned'
                  ? reopen(run.classIndex, seat.name)
                  : act('pinned', t('已釘選')),
            },
            { label: t('確認已處理'), onSelect: () => act('ack', t('已標記處理')) },
            {
              label: `${t('暫緩')} ${SNOOZE_MINUTES} ${t('分鐘')}`,
              onSelect: () =>
                act(
                  'snoozed',
                  `${t('已暫緩')} ${SNOOZE_MINUTES} ${t('分鐘')}`,
                  elapsed + SNOOZE_MINUTES,
                ),
            },
            { label: t('這堂課不再提醒'), onSelect: () => act('ignored', t('這堂課不再提醒')) },
            'divider',
            {
              label: t('復原'),
              onSelect: () => {
                reopen(run.classIndex, seat.name);
                notify(`${seat.name} ${t('已復原')}`);
              },
            },
          ]}
        />
      ),
    });
  };

  const row = (seat: SeatRecord) => {
    const look = seatPresentation(seat.status);
    const alert = alertOf(seat);
    return {
      key: seat.name,
      onSelect: () => openItem({ k: 'iv', n: seat.name }),
      cells: tableCells(
        <NameCell icon={seat.name[0]} text={seat.name} tone={seat.status === 'need' ? 'e' : ''} />,
        <span className="sgcell">
          <StatusText glyph={look.glyph} label={seatStatusLabel(seat, t)} tone={look.tone} />
          <SeatSignals alert={alert} seat={seat} t={t} />
        </span>,
        <span className="vmut">{`${t('第')} ${seatQuestion(seat, total)} / ${total}`}</span>,
        <ProgressBars level={Math.max(1, Math.ceil((seatQuestion(seat, total) / total) * 3))} />,
        <>
          {look.needsTeacher && !isMuted(alert, elapsed) ? (
            <button
              className="bt sm pri"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                openItem({ k: 'iv', n: seat.name });
              }}
            >
              {t('介入')}
            </button>
          ) : null}
          {look.needsTeacher ? (
            <button
              className="vcog"
              style={{ opacity: 1 }}
              title={t('處理這則提醒')}
              type="button"
              onClick={(event) => openAlertMenu(event, seat)}
            >
              <GearIcon />
            </button>
          ) : null}
        </>,
      ),
    };
  };

  return (
    <Page>
      <PageHeader
        actions={
          <>
            <ToolbarIconButton
              title={t('QR 與學生連結')}
              onClick={() => openModal(<QrInviteDialog id={run.id} kind="run" />)}
            >
              <QrIcon />
            </ToolbarIconButton>
            <button
              className="bt"
              type="button"
              onClick={() => openModal(<AgentPolicyDialog runId={run.id} />)}
            >
              {t('調整 AI 助教')}
            </button>
            <button
              className="bt"
              type="button"
              onClick={() => openModal(<EndSessionDialog runId={run.id} />)}
            >
              {t('結束')}
            </button>
          </>
        }
        meta={`${classes[run.classIndex].name} · ${seats.length} ${t('人')} · ${t('每 2 秒更新')}`}
        title={runName(activities, run)}
      />
      <div className="vtbar">
        <span className="vsumline">
          <b>{count('ok')}</b> {t('作答中')} <em>·</em> <b>{count('online')}</b> {t('線上')}{' '}
          <em>·</em> <b>{count('offline')}</b> {t('離線')} <em>·</em>{' '}
          {needing ? (
            <>
              <b className="hot">{needing}</b> <span className="hot">{t('需要你')}</span>
            </>
          ) : (
            t('沒有人需要你')
          )}
        </span>
        <div style={{ width: 10 }} />
        <FilterBar
          definitions={filterDefinitions('live', classes, myClassIndexes(classes))}
          scope="live"
        />
      </div>
      <PageBody>
        {groups.map(([label, predicate]) => {
          const rows = seats.filter(predicate);
          if (!rows.length) return null;
          return (
            <div key={label}>
              <GroupHeading count={rows.length}>{label}</GroupHeading>
              <VTable rows={rows.map(row)} template="minmax(0,1fr) 190px 130px 46px 80px" />
            </div>
          );
        })}
      </PageBody>
    </Page>
  );
}
